import queryString from "query-string";
import { useCallback, useEffect } from "react";
import type { Params } from "react-router-dom";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import { inboundRoute, mrsLink } from "../links";
import { Analytics } from "@helium10/re-features";

export const useRedirect = () => {
  const navigate = useNavigate();

  const getPath = useCallback(
    <S extends string>(
      url: string,
      query?: Record<string, string | number>,
      params?: Params<S>,
    ) => {
      const path = queryString.parseUrl(url).url;
      return queryString.stringifyUrl({
        url: generatePath(path, { ...params }),
        query: {
          ...query,
        },
      });
    },
    [],
  );

  const redirect = useCallback(
    <S extends string>(
      url: string,
      query?: Record<string, string | number>,
      params?: Params<S>,
      force?: boolean,
    ) => {
      const path = getPath(url, query, params);
      navigate(path, { replace: force });
    },
    [getPath, navigate],
  );

  return { redirect, getPath };
};

// http://localhost:1981/managed-refund-service?accountId=1543265037&sesource=weeklyReportEmailReviewMyClaims
// http://localhost:1981/managed-refund-service/inbound?accountId=1543265037&sesource=inboundEmailStartClaim
interface IRouteSegmentEvent {
  eventName: string;
  source: Record<string, string>;
}

const routeSegmentEventMapping: Record<string, IRouteSegmentEvent> = {
  [mrsLink]: {
    eventName: "View Claim Overview",
    source: {
      "": "", // 刷新页面
      weeklyReportEmailReviewMyClaims: "Weekly Report Email Review My Claims", // 邮件内链接进入inbound
    },
  },
  [inboundRoute]: {
    eventName: "View Inbound",
    source: {
      "": "", // 刷新页面
      helium10HomeDashboardStartClaim: "Helium 10 Home Dashboard Start Claim", // 首页banner进入inbound
      inboundEmailStartClaim: "Inbound Email Start Claim", // 邮件内链接进入inbound
    },
  },
};

const sekey = "sesource"; // url中写

export const useSegmentEevntRedirect = () => {
  const location = useLocation();

  useEffect(() => {
    const globalRouteSERedirect = async () => {
      // 只有配置了的页面才有进入页面记录埋点(重定向埋点),否则没有(不做任何操作)
      const routeSEConfig = routeSegmentEventMapping[location.pathname];
      if (routeSEConfig) {
        const searchParams = new URLSearchParams(location.search);
        const source = searchParams.get(sekey) ?? "";

        // 发送埋点请求
        Analytics.track(routeSEConfig.eventName, {
          Source: routeSEConfig.source[source],
        });

        // 是否需要删除 url 中 source 相关内容(重定向)
        if (routeSEConfig.source[source]) {
          searchParams.delete(sekey);
          const newSearch = searchParams.toString() ? `?${searchParams.toString()}` : "";
          // 构造新 URL（保留其他参数，仅移除目标参数）
          const newUrl = `${location.pathname}${newSearch}${location.hash}`;
          // 替换URL,埋点可以用该方法,但是页面跳转不能用,该方法不会立即改变react router的location对象
          history.replaceState(null, "", newUrl);
        }
      }
    };

    globalRouteSERedirect();
  }, [location.pathname]);
};
